import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Buildings, CaretDown, CaretLeft, CaretRight } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../../components/SearchModeBar';
import { IEmpresaFechamentoListItem, useFechamentos } from '../../../../../hooks/useFechamentos';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';
import { formatDateBR, formatDays } from '../helpers';
import { FechamentoBadge } from './FechamentoBadge';
import { InfoPopover } from './InfoPopover';

type SortField = 'nome' | 'cnpj' | 'dt_cli_desde' | 'dt_cli_ate' | 'regime' | 'contabilidade';
type SortDirection = 'asc' | 'desc';

type SearchMode = 'ID' | 'Nome' | 'CNPJ';

const PAGE_SIZE = 50;

interface EmpresasFechamentoTableProps {
  externalFilters?: Record<string, string | number | boolean | undefined>;
  // eslint-disable-next-line no-unused-vars
  onSelectEmpresa?: (empresa: IEmpresaFechamentoListItem) => void;
}

export const EmpresasFechamentoTable = ({
  externalFilters = {},
  onSelectEmpresa,
}: EmpresasFechamentoTableProps) => {
  const { getEmpresas } = useFechamentos();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('ID');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('nome');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Debounce do search: aplica searchTerm ao queryFilters apos 400ms parado.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset de pagina quando filtros externos mudam.
  // searchMode foi movido pro onChange do Select pra evitar double-request:
  // searchMode e dep do queryFilters useMemo, entao mudar+resetar via useEffect
  // disparava 2 requests (com pagina antiga e nova).
  useEffect(() => {
    setCurrentPage(1);
  }, [externalFilters]);

  const queryFilters = useMemo(() => {
    const f: Record<string, string | number | boolean> = {
      ...externalFilters,
      page: currentPage,
      size: PAGE_SIZE,
      order_by: sortField,
      order_direction: sortDirection,
    };
    if (debouncedSearch) {
      if (searchMode === 'ID') {
        f.external_id = debouncedSearch;
      } else if (searchMode === 'Nome') {
        f.razao_social = debouncedSearch;
      } else if (searchMode === 'CNPJ') {
        f.cnpj = debouncedSearch.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      }
    }
    return f;
  }, [externalFilters, currentPage, sortField, sortDirection, debouncedSearch, searchMode]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['fechamento-empresas', queryFilters],
    queryFn: getEmpresas,
    staleTime: 2 * 60 * 1000,
    placeholderData: prev => prev, // mantem dados antigos durante refetch — evita flicker na paginacao
  });

  const empresas: IEmpresaFechamentoListItem[] = data?.data?.items ?? [];
  const totalPages = data?.data?.pages ?? 1;
  const currentBackendPage = data?.data?.page ?? 1;

  return (
    <GlassCard intensity="medium" p={6}>
      {/* Toolbar acima do titulo: Select | Input */}
      <Flex gap={3} mb={4} align="center" wrap="wrap" direction={{ base: 'column', md: 'row' }}>
        <Flex
          gap={2}
          maxW={{ base: '100%', md: '420px' }}
          w={{ base: 'full', md: 'auto' }}
          flex={{ base: '1', md: '0 0 420px' }}
        >
          <SearchModeBar
            modes={MODES_ID_NOME_CNPJ}
            mode={searchMode}
            onModeChange={m => {
              setSearchMode(m as SearchMode);
              // Limpa o termo: senao um "Empresa X" digitado em modo Nome
              // dispararia request `external_id=Empresa X` ao trocar pra ID.
              setSearchTerm('');
              setDebouncedSearch('');
              setCurrentPage(1); // reset sincrono evita double request
            }}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
        </Flex>
      </Flex>

      <Heading size="md" color="gray.700" mb={4}>
        <Flex align="center" gap={2}>
          <Icon as={Buildings} />
          Empresas
          <InfoPopover title="Listagem de empresas">
            Empresas (ativas e inativas) com seu status de fechamento contábil. O status é calculado
            automaticamente comparando a última data de fechamento com a data esperada (mês passado
            para clientes ativos; mês anterior à saída para inativos). Clique numa linha para ver o
            detalhe completo.
          </InfoPopover>
        </Flex>
      </Heading>

      {/* Toolbar de ordenacao */}
      <Flex align="center" gap={2} mb={4} flexWrap="wrap">
        <Text fontSize="xs" color="#94A3B8" fontWeight="medium" mr={1}>
          Ordenar por:
        </Text>
        <Select
          size="sm"
          maxW="200px"
          borderRadius="md"
          value={sortField}
          onChange={e => {
            setSortField(e.target.value as SortField);
            setCurrentPage(1);
          }}
          icon={<CaretDown size={14} />}
        >
          <option value="nome">Nome</option>
          <option value="cnpj">CNPJ</option>
          <option value="dt_cli_desde">Cliente desde</option>
          <option value="dt_cli_ate">Cliente até</option>
          <option value="regime">Regime</option>
          <option value="contabilidade">Status contábil</option>
        </Select>
        <IconButton
          aria-label="Inverter direção"
          icon={
            sortDirection === 'asc' ? (
              <ArrowUp size={14} weight="bold" />
            ) : (
              <ArrowDown size={14} weight="bold" />
            )
          }
          size="sm"
          variant="outline"
          onClick={() => {
            setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
            setCurrentPage(1);
          }}
        />
      </Flex>

      {isError && (
        <Alert status="error" mb={4} borderRadius="lg">
          <AlertIcon />
          Erro ao carregar empresas. Verifique sua conexão ou tente novamente.
        </Alert>
      )}

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner size="lg" color="blue.500" thickness="3px" />
        </Flex>
      ) : (
        <>
          {empresas.length === 0 && !isError ? (
            <Flex justify="center" py={16}>
              <Text color="#94A3B8" fontSize="sm">
                Nenhuma empresa encontrada com os filtros atuais.
              </Text>
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {empresas.map(emp => (
                <EmpresaCard key={emp.id} emp={emp} onClick={() => onSelectEmpresa?.(emp)} />
              ))}
            </SimpleGrid>
          )}

          {/* Paginacao */}
          {data?.data && totalPages > 1 && (
            <Flex
              justify="space-between"
              align="center"
              mt={6}
              pt={4}
              borderTopWidth="1px"
              borderColor="rgba(15, 23, 42, 0.08)"
            >
              <IconButton
                aria-label="Página anterior"
                icon={<CaretLeft size={18} />}
                size="sm"
                variant="outline"
                isDisabled={!data.data.previous_page}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                borderRadius="full"
              />
              <Text fontSize="sm" color="#71717A" fontWeight="medium">
                Página{' '}
                <Text as="span" color="#0F172A" fontWeight="700">
                  {currentBackendPage}
                </Text>{' '}
                de{' '}
                <Text as="span" color="#0F172A" fontWeight="700">
                  {totalPages}
                </Text>{' '}
                <Text as="span" color="#94A3B8">
                  · {data.data.total} empresas
                </Text>
              </Text>
              <IconButton
                aria-label="Próxima página"
                icon={<CaretRight size={18} />}
                size="sm"
                variant="outline"
                isDisabled={!data.data.next_page}
                onClick={() => setCurrentPage(prev => prev + 1)}
                borderRadius="full"
              />
            </Flex>
          )}
        </>
      )}
    </GlassCard>
  );
};

// ============================================================================
// Card individual de empresa
// ============================================================================

// React.memo evita re-render dos 50 cards quando a pagina pai muda state
// nao relacionado (filtros, ordenacao, etc).
const EmpresaCard = React.memo(
  ({
    emp,
    onClick,
  }: {
    emp: IEmpresaFechamentoListItem;
    // eslint-disable-next-line no-unused-vars
    onClick: () => void;
  }) => {
    const isInativa = !!emp.dt_cli_ate;

    return (
      <GlassCard intensity="medium" hoverable onClick={onClick} cursor="pointer" p={4}>
        <Stack spacing={3}>
          {/* Topo: nome + badges */}
          <Box>
            <Flex align="center" gap={2} mb={1.5} flexWrap="wrap">
              <Badge colorScheme="blue" fontSize="10px" borderRadius="full" px={2} fontWeight="600">
                {emp.external_id || '-'}
              </Badge>
              {isInativa && (
                <Badge
                  colorScheme="red"
                  fontSize="10px"
                  borderRadius="full"
                  px={2}
                  fontWeight="600"
                >
                  Inativa
                </Badge>
              )}
              {emp.regime && (
                <Badge
                  colorScheme="purple"
                  variant="subtle"
                  fontSize="10px"
                  borderRadius="full"
                  px={2}
                  fontWeight="500"
                >
                  {emp.regime}
                </Badge>
              )}
            </Flex>
            <Tooltip label={emp.razao_social || emp.nome || '-'} placement="top" hasArrow>
              <Heading
                as="h3"
                fontSize="sm"
                fontWeight="600"
                color="#0F172A"
                lineHeight="1.3"
                isTruncated
              >
                {emp.nome || emp.razao_social || '(sem nome)'}
              </Heading>
            </Tooltip>
            {emp.cnpj && (
              <Text fontSize="11px" color="#94A3B8" mt={0.5}>
                {formatCNPJ(emp.cnpj)}
              </Text>
            )}
          </Box>

          {/* Status contabil em destaque */}
          <Box>
            <FechamentoBadge info={emp.contabil} size="md" />
          </Box>

          {/* Rodape: cliente desde / ate */}
          <Flex
            align="center"
            justify="space-between"
            pt={2}
            borderTopWidth="1px"
            borderColor="rgba(15, 23, 42, 0.06)"
          >
            <Box>
              <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="600">
                Cliente desde
              </Text>
              <Text fontSize="11px" color="#0F172A" fontWeight="500">
                {formatDateBR(emp.dt_cli_desde)}
              </Text>
              {emp.dias_como_cliente !== null && (
                <Text fontSize="10px" color="#94A3B8">
                  há {formatDays(emp.dias_como_cliente)}
                </Text>
              )}
            </Box>
            <Box textAlign="right">
              <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="600">
                Cliente até
              </Text>
              <Text
                fontSize="11px"
                color={isInativa ? '#DC2626' : '#94A3B8'}
                fontWeight={isInativa ? '600' : '400'}
              >
                {emp.dt_cli_ate ? formatDateBR(emp.dt_cli_ate) : '—'}
              </Text>
            </Box>
          </Flex>
        </Stack>
      </GlassCard>
    );
  },
);
EmpresaCard.displayName = 'EmpresaCard';
